import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { useCart, CartProvider } from '../../contexts/CartContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Toaster } from 'sonner';
import { formatCurrency } from '../../lib/utils';
import { ShoppingCart, Plus, Minus, Trash2, MapPin, Phone, Clock, X, Check, Package, AlertTriangle, MessageCircle, Truck, Navigation, Copy, QrCode, Layers, ChevronRight, Star, Flame } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Tiny sub-components ─── */

function BannerCarousel({ banners, onBannerClick }) {
  const scrollRef = useRef(null);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (banners.length <= 1) return;
    const interval = setInterval(() => {
      setCurrent(prev => (prev + 1) % banners.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [banners.length]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ left: current * scrollRef.current.offsetWidth, behavior: 'smooth' });
    }
  }, [current]);

  if (!banners.length) return null;

  return (
    <div className="relative mb-4" data-testid="banner-carousel">
      <div ref={scrollRef} className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
        {banners.map((banner, i) => (
          <div
            key={banner.id}
            className="snap-center flex-shrink-0 w-full px-3 cursor-pointer"
            onClick={() => onBannerClick(banner)}
            data-testid={`promo-banner-${banner.id}`}
          >
            <div className="relative rounded-xl overflow-hidden shadow-md" style={{ aspectRatio: '2/1' }}>
              <img
                src={banner.image_url}
                alt={banner.title || 'Promoção'}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = ''; e.target.className = 'w-full h-full bg-gradient-to-r from-red-400 to-orange-400'; }}
              />
              {banner.title && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                  <p className="text-white font-semibold text-sm">{banner.title}</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {banners.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-2">
          {banners.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`h-1.5 rounded-full transition-all ${i === current ? 'w-5 bg-slate-700' : 'w-1.5 bg-slate-300'}`}
              data-testid={`banner-dot-${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FeaturedGrid({ products, store, onProductClick }) {
  if (!products.length) return null;

  return (
    <div className="px-3 mb-4" data-testid="featured-section">
      <div className="flex items-center gap-2 mb-3">
        <Star className="w-4 h-4 text-amber-500" />
        <h2 className="font-bold text-slate-800 text-base">Destaques</h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {products.map(product => {
          const hasVariations = product.has_variations && product.variations?.length > 0;
          const minPrice = hasVariations ? Math.min(...product.variations.map(v => v.price)) : product.price;
          const showSale = product.is_on_sale && product.sale_price;

          return (
            <div
              key={product.id}
              className="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-100 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => onProductClick(product)}
              data-testid={`featured-product-${product.id}`}
            >
              <div className="relative aspect-square">
                {product.image_url ? (
                  <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                    <Package className="w-8 h-8 text-slate-300" />
                  </div>
                )}
                {showSale && (
                  <div className="absolute top-1.5 left-1.5 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                    <Flame className="w-3 h-3" /> PROMO
                  </div>
                )}
              </div>
              <div className="p-2.5">
                <h3 className="font-semibold text-slate-800 text-sm line-clamp-2 leading-tight">{product.name}</h3>
                <div className="mt-1.5">
                  {showSale ? (
                    <div className="flex flex-col">
                      <span className="text-[11px] text-slate-400 line-through">{formatCurrency(product.price)}</span>
                      <span className="font-bold text-red-500 text-sm">{formatCurrency(product.sale_price)}</span>
                    </div>
                  ) : (
                    <span className="font-bold text-sm" style={{ color: store.primary_color }}>
                      {hasVariations && <span className="text-[10px] font-normal text-slate-400 block">a partir de</span>}
                      {formatCurrency(minPrice)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CategoryNav({ categories, activeCategory, onSelect, primaryColor, hasHomePage }) {
  const scrollRef = useRef(null);

  return (
    <div className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm" data-testid="category-nav">
      <div ref={scrollRef} className="flex overflow-x-auto scrollbar-hide px-3 py-2 gap-2" style={{ scrollbarWidth: 'none' }}>
        {hasHomePage && (
          <button
            onClick={() => onSelect(null)}
            className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              activeCategory === null ? 'text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            style={activeCategory === null ? { backgroundColor: primaryColor } : {}}
            data-testid="category-tab-home"
          >
            Início
          </button>
        )}
        {categories.map(cat => {
          const isActive = activeCategory === cat.id;
          return (
            <button
              key={cat.id}
              onClick={() => onSelect(cat.id)}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                isActive ? 'text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              style={isActive ? { backgroundColor: primaryColor } : {}}
              data-testid={`category-tab-${cat.id}`}
            >
              {cat.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ProductCard({ product, store, onClick }) {
  const productAddons = product.all_addons || product.addons || [];
  const hasAddons = productAddons.length > 0;
  const hasVariations = product.has_variations && product.variations?.length > 0;
  const minPrice = hasVariations ? Math.min(...product.variations.map(v => v.price)) : product.price;
  const showSale = product.is_on_sale && product.sale_price;

  return (
    <div
      className="flex gap-3 p-3 bg-white cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
      onClick={onClick}
      data-testid={`product-${product.id}`}
    >
      <div className="relative w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
        {product.image_url ? (
          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-slate-100 flex items-center justify-center">
            <Package className="w-6 h-6 text-slate-300" />
          </div>
        )}
        {showSale && (
          <div className="absolute top-1 left-1 bg-red-500 text-white text-[9px] font-bold px-1 py-0.5 rounded">
            PROMO
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-slate-800 text-sm">{product.name}</h3>
        {product.description && (
          <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{product.description}</p>
        )}
        <div className="mt-1.5 flex items-center gap-2">
          {showSale ? (
            <>
              <span className="text-xs text-slate-400 line-through">{formatCurrency(product.price)}</span>
              <span className="font-bold text-red-500 text-sm">{formatCurrency(product.sale_price)}</span>
            </>
          ) : (
            <span className="font-bold text-sm" style={{ color: store.primary_color }}>
              {hasVariations && <span className="text-[10px] font-normal text-slate-400 mr-1">a partir de</span>}
              {formatCurrency(minPrice)}
            </span>
          )}
        </div>
        {hasVariations && (
          <p className="text-[10px] text-indigo-600 flex items-center gap-0.5 mt-0.5">
            <Layers className="w-3 h-3" /> {product.variations.length} variações
          </p>
        )}
        {hasAddons && (
          <p className="text-[10px] text-slate-400 mt-0.5">
            + adicionais
            {product.free_addons_limit > 0 && (
              <span className="ml-1 text-green-600 font-medium">({product.free_addons_limit} grátis)</span>
            )}
          </p>
        )}
      </div>
      <div className="flex items-center">
        <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: `${store.primary_color}15` }}>
          <Plus className="w-4 h-4" style={{ color: store.primary_color }} />
        </div>
      </div>
    </div>
  );
}

/* ─── Main Store Content ─── */

function StoreContent() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { items, addItem, updateQuantity, removeItem, clearCart, getTotal, getItemCount, getItemPrice, isOpen, setIsOpen } = useCart();
  const [store, setStore] = useState(null);
  const [storeStatus, setStoreStatus] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productQuantity, setProductQuantity] = useState(1);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [productNotes, setProductNotes] = useState('');
  const [showCheckout, setShowCheckout] = useState(false);
  const [deliverySettings, setDeliverySettings] = useState({ default_fee: 0, free_threshold: null });
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [checkoutData, setCheckoutData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_address: '',
    delivery_type: 'delivery',
    payment_method: 'cash',
    notes: ''
  });

  useEffect(() => {
    fetchStore();
  }, [slug]);

  const fetchStore = async () => {
    try {
      const response = await axios.get(`${API}/loja/${slug}`);
      setStore(response.data.establishment);
      setStoreStatus(response.data.store_status);
      setCategories(response.data.categories);
      setProducts(response.data.products);
      setFeaturedProducts(response.data.featured_products || []);
      setBanners(response.data.promo_banners || []);
      setDeliverySettings(response.data.delivery_settings || { default_fee: 0, free_threshold: null });

      const establishment = response.data.establishment;
      if (establishment.use_delivery_zones && establishment.delivery_zones?.length > 0) {
        setDeliveryZones(establishment.delivery_zones);
      }

      if (response.data.categories.length > 0) {
        // Only show home page if there are featured products or banners
        const hasFeatured = (response.data.featured_products || []).length > 0;
        const hasBanners = (response.data.promo_banners || []).length > 0;
        if (hasFeatured || hasBanners) {
          setActiveCategory(null); // Start on home page
        } else {
          setActiveCategory(response.data.categories[0].id); // Go to first category
        }
      }

      const root = document.documentElement;
      root.style.setProperty('--brand-primary', response.data.establishment.primary_color);
      root.style.setProperty('--brand-secondary', response.data.establishment.secondary_color);
    } catch (err) {
      setError(err.response?.data?.detail || 'Loja não encontrada');
    } finally {
      setLoading(false);
    }
  };

  /* ─── Delivery helpers (unchanged logic) ─── */
  const calculateDeliveryFee = () => {
    if (checkoutData.delivery_type === 'pickup') return 0;
    const subtotal = getTotal();
    const { default_fee, free_threshold } = deliverySettings;
    if (free_threshold && free_threshold > 0 && subtotal >= free_threshold) return 0;
    if (deliveryZones.length > 0 && selectedZone) {
      const zone = deliveryZones.find(z => z.id === selectedZone);
      return zone ? zone.fee : default_fee || 0;
    }
    return default_fee || 0;
  };

  const getSelectedZoneName = () => {
    if (!selectedZone) return null;
    const zone = deliveryZones.find(z => z.id === selectedZone);
    return zone?.name || null;
  };

  const getDeliveryInfo = () => {
    const subtotal = getTotal();
    const { default_fee, free_threshold } = deliverySettings;
    if (checkoutData.delivery_type === 'pickup') return { fee: 0, isFree: true, reason: 'pickup' };
    if (free_threshold && free_threshold > 0 && subtotal >= free_threshold) return { fee: 0, isFree: true, reason: 'threshold' };
    const remaining = free_threshold ? free_threshold - subtotal : null;
    return { fee: default_fee || 0, isFree: false, remaining, threshold: free_threshold };
  };

  const getOrderTotal = () => getTotal() + calculateDeliveryFee();

  /* ─── Product modal helpers ─── */
  const openProductModal = (product) => {
    setSelectedProduct(product);
    setProductQuantity(1);
    setSelectedAddons([]);
    setSelectedVariation(null);
    setProductNotes('');
  };

  const handleBannerClick = (banner) => {
    const product = products.find(p => p.id === banner.product_id);
    if (product) openProductModal(product);
  };

  const handleAddonToggle = (addon) => {
    setSelectedAddons(prev => {
      const existing = prev.find(a => a.id === addon.id);
      if (existing) return prev.filter(a => a.id !== addon.id);
      return [...prev, { ...addon, quantity: 1 }];
    });
  };

  const handleAddToCart = () => {
    if (!selectedProduct) return;
    if (selectedProduct.has_variations && selectedProduct.variations?.length > 0 && !selectedVariation) {
      toast.error('Selecione uma variação para continuar');
      return;
    }
    addItem(selectedProduct, productQuantity, selectedAddons, productNotes, selectedVariation);
    toast.success('Item adicionado ao carrinho!');
    setSelectedProduct(null);
  };

  const calculateAddonsTotal = (addons, freeLimit) => {
    if (!addons || addons.length === 0) return 0;
    const premiumAddons = addons.filter(a => a.is_premium);
    const regularAddons = addons.filter(a => !a.is_premium);
    const sortedRegular = [...regularAddons].sort((a, b) => a.price - b.price);
    let count = 0, total = 0;
    for (const addon of premiumAddons) { for (let i = 0; i < addon.quantity; i++) total += addon.price; }
    for (const addon of sortedRegular) { for (let i = 0; i < addon.quantity; i++) { count++; if (count > freeLimit) total += addon.price; } }
    return total;
  };

  const getProductTotal = () => {
    if (!selectedProduct) return 0;
    const basePrice = selectedVariation ? selectedVariation.price : selectedProduct.price;
    const freeLimit = selectedProduct.free_addons_limit || 0;
    return basePrice * productQuantity + calculateAddonsTotal(selectedAddons, freeLimit) * productQuantity;
  };

  const isAddonFree = (addon) => {
    if (!selectedProduct || addon.is_premium) return false;
    const freeLimit = selectedProduct.free_addons_limit || 0;
    if (freeLimit === 0) return false;
    const nonPremiumSelected = selectedAddons.filter(a => !a.is_premium);
    const otherAddons = nonPremiumSelected.filter(a => a.id !== addon.id);
    const otherCount = otherAddons.reduce((sum, a) => sum + a.quantity, 0);
    const existingIndex = nonPremiumSelected.findIndex(a => a.id === addon.id);
    if (existingIndex > -1) return (otherCount + 1) <= freeLimit;
    return otherCount < freeLimit;
  };

  const getRemainingFreeAddons = () => {
    if (!selectedProduct) return 0;
    const freeLimit = selectedProduct.free_addons_limit || 0;
    const nonPremiumSelected = selectedAddons.filter(a => !a.is_premium);
    const selectedCount = nonPremiumSelected.reduce((sum, a) => sum + a.quantity, 0);
    return Math.max(0, freeLimit - selectedCount);
  };

  const getProductAddons = (product) => product.all_addons || product.addons || [];

  /* ─── Category filtering ─── */
  const getProductsByCategory = (categoryId) => products.filter(p => p.category_id === categoryId);
  const activeCategoryProducts = activeCategory ? getProductsByCategory(activeCategory) : [];
  const activeCategoryName = categories.find(c => c.id === activeCategory)?.name || '';

  /* ─── Checkout ─── */
  const handleCheckout = async (e) => {
    e.preventDefault();
    if (checkoutData.delivery_type === 'delivery' && deliveryZones.length > 0 && !selectedZone) {
      toast.error('Por favor, selecione sua região de entrega');
      return;
    }
    try {
      const orderItems = items.map(item => {
        const orderItem = {
          product_id: item.product.id,
          product_name: item.product.name,
          quantity: item.quantity,
          unit_price: item.selectedVariation ? item.selectedVariation.price : item.product.price,
          addons: item.selectedAddons.map(addon => ({ addon_id: addon.id, name: addon.name, price: addon.price, quantity: addon.quantity })),
          notes: item.notes
        };
        if (item.selectedVariation) {
          orderItem.variation = { variation_id: item.selectedVariation.id, name: item.selectedVariation.name, price: item.selectedVariation.price };
        }
        return orderItem;
      });

      const zoneName = getSelectedZoneName();
      const customerAddress = checkoutData.delivery_type === 'delivery'
        ? (zoneName ? `[${zoneName}] ${checkoutData.customer_address}` : checkoutData.customer_address)
        : '';

      const response = await axios.post(`${API}/loja/${slug}/orders`, {
        ...checkoutData,
        customer_address: customerAddress,
        establishment_id: store.id,
        items: orderItems,
        delivery_zone_id: selectedZone || null
      });

      clearCart();
      setShowCheckout(false);
      setIsOpen(false);
      setSelectedZone(null);
      navigate(`/acompanhar/${response.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar pedido');
    }
  };

  const getWhatsAppLink = () => {
    if (!store?.whatsapp) return null;
    const phone = store.whatsapp.replace(/\D/g, '');
    const message = encodeURIComponent(`Olá! Vi que a loja ${store.name} está fechada. Gostaria de saber quando voltam a atender.`);
    return `https://wa.me/${phone}?text=${message}`;
  };

  /* ─── Loading / Error states ─── */
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: 'var(--brand-primary, #ef4444)' }}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-slate-50">
        <Package className="w-16 h-16 text-slate-300 mb-4" />
        <h1 className="text-2xl font-bold text-slate-800 mb-2">Loja não encontrada</h1>
        <p className="text-slate-500">{error}</p>
      </div>
    );
  }

  const isStoreOpen = storeStatus?.is_open !== false;
  const isStoreBlocked = storeStatus?.is_blocked === true;

  /* ─── RENDER ─── */
  return (
    <div className="store-wrapper bg-slate-50 min-h-screen" data-testid="store-page">
      <Toaster position="top-center" />

      {/* Blocked Store */}
      {isStoreBlocked && (
        <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-slate-100" data-testid="store-blocked-banner">
          <div className="max-w-md text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 mb-3">Loja Temporariamente Indisponível</h1>
            <p className="text-slate-600 mb-6">{storeStatus?.blocked_reason || 'Esta loja está temporariamente indisponível.'}</p>
            {store?.whatsapp && (
              <a href={`https://wa.me/${store.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Olá! Vi que a loja ${store.name} está indisponível.`)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-green-500 text-white px-6 py-3 rounded-lg hover:bg-green-600 transition-colors">
                <MessageCircle className="w-5 h-5" /> Entrar em contato
              </a>
            )}
          </div>
        </div>
      )}

      {/* Normal Store Content */}
      {!isStoreBlocked && (
        <>
          {/* Closed Banner */}
          {!isStoreOpen && (
            <div className="bg-red-500 text-white py-3 px-4" data-testid="store-closed-banner">
              <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-6 h-6 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">Loja Fechada no Momento</p>
                    <p className="text-sm text-red-100">
                      {storeStatus?.reopen_time ? `Previsão de reabertura: ${storeStatus.reopen_time}` : 'Você pode ver o cardápio, mas não é possível fazer pedidos agora.'}
                    </p>
                  </div>
                </div>
                {store?.whatsapp && (
                  <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 rounded-lg text-sm font-medium transition-colors" data-testid="whatsapp-closed-btn">
                    <MessageCircle className="w-4 h-4" /> Falar no WhatsApp
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Hero Header */}
          <div
            className="relative h-36 sm:h-44"
            style={{
              backgroundImage: store.banner_url
                ? `url(${store.banner_url})`
                : `linear-gradient(135deg, ${store.primary_color}, ${store.secondary_color})`,
              backgroundSize: 'cover', backgroundPosition: 'center'
            }}
            data-testid="store-hero"
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4 text-white z-10">
              <div className="flex items-end gap-3 max-w-4xl mx-auto">
                {store.logo_url && (
                  <img src={store.logo_url} alt={store.name} className="w-16 h-16 rounded-xl object-cover border-2 border-white shadow-lg" />
                )}
                <div className="flex-1 min-w-0">
                  <h1 className="text-xl font-bold truncate">{store.name}</h1>
                  {store.description && <p className="text-xs opacity-90 line-clamp-1">{store.description}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Store Info Bar */}
          <div className="bg-white px-4 py-2.5 border-b flex flex-wrap gap-3 text-xs text-slate-500">
            {store.address && (
              <div className="flex items-center gap-1"><MapPin className="w-3 h-3" /><span className="truncate max-w-[180px]">{store.address}</span></div>
            )}
            {store.phone && (
              <div className="flex items-center gap-1"><Phone className="w-3 h-3" /><span>{store.phone}</span></div>
            )}
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span className={isStoreOpen ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                {isStoreOpen ? 'Aberto' : 'Fechado'}
              </span>
              {storeStatus?.today_schedule && isStoreOpen && storeStatus.closes_at && (
                <span className="text-slate-400">(até {storeStatus.closes_at})</span>
              )}
            </div>
          </div>

          {/* Category Navigation Tabs */}
          {categories.length > 0 && (
            <CategoryNav
              categories={categories}
              activeCategory={activeCategory}
              onSelect={setActiveCategory}
              primaryColor={store.primary_color}
              hasHomePage={featuredProducts.length > 0 || banners.length > 0}
            />
          )}

          {/* Main Content Area */}
          <div className="max-w-4xl mx-auto pb-24">
            {/* HOME VIEW: Banners + Featured (only when no category selected) */}
            {activeCategory === null && (
              <div data-testid="home-section">
                {/* Banners Carousel */}
                {banners.length > 0 && (
                  <div className="pt-3">
                    <BannerCarousel banners={banners} onBannerClick={handleBannerClick} />
                  </div>
                )}

                {/* Featured Products Grid (2 cols mobile) */}
                {featuredProducts.length > 0 && (
                  <FeaturedGrid products={featuredProducts} store={store} onProductClick={openProductModal} />
                )}

                {/* If no featured and no banners, show all products as fallback */}
                {featuredProducts.length === 0 && banners.length === 0 && (
                  <div className="p-8 text-center text-slate-400 text-sm">
                    Selecione uma categoria acima para ver os produtos
                  </div>
                )}
              </div>
            )}

            {/* CATEGORY VIEW: Product list (only when a category is selected) */}
            {activeCategory !== null && (
              <div data-testid="category-products-section">
                <div className="flex items-center gap-2 px-4 py-3 bg-white border-b border-slate-100">
                  <h2 className="font-bold text-slate-800 text-base">{activeCategoryName}</h2>
                  <span className="text-xs text-slate-400">({activeCategoryProducts.length})</span>
                </div>
                <div className="bg-white">
                  {activeCategoryProducts.length === 0 ? (
                    <div className="p-8 text-center text-slate-400 text-sm">Nenhum produto disponível nesta categoria</div>
                  ) : (
                    activeCategoryProducts.map(product => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        store={store}
                        onClick={() => openProductModal(product)}
                      />
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Cart FAB */}
          {getItemCount() > 0 && (
            <div className="fixed bottom-4 left-4 right-4 z-30 max-w-lg mx-auto" data-testid="cart-fab-wrapper">
              <button
                className="w-full flex items-center justify-between px-5 py-3.5 rounded-xl text-white shadow-lg active:scale-[0.98] transition-transform"
                style={{ backgroundColor: store.primary_color }}
                onClick={() => setIsOpen(true)}
                data-testid="cart-fab"
              >
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <ShoppingCart className="w-5 h-5" />
                    <span className="absolute -top-2 -right-2 bg-white text-xs font-bold w-4 h-4 rounded-full flex items-center justify-center" style={{ color: store.primary_color }}>
                      {getItemCount()}
                    </span>
                  </div>
                  <span className="font-medium text-sm">Ver carrinho</span>
                </div>
                <span className="font-bold">{formatCurrency(getTotal())}</span>
              </button>
            </div>
          )}

          {/* ─── Product Modal ─── */}
          <Dialog open={!!selectedProduct} onOpenChange={() => setSelectedProduct(null)}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-modal">
              {selectedProduct && (
                <>
                  {selectedProduct.image_url && (
                    <img src={selectedProduct.image_url} alt={selectedProduct.name} className="w-full h-48 object-cover rounded-lg -mt-2" />
                  )}
                  <DialogHeader>
                    <DialogTitle>{selectedProduct.name}</DialogTitle>
                  </DialogHeader>
                  <p className="text-slate-600">{selectedProduct.description}</p>

                  {/* Price */}
                  <div className="flex items-center gap-2">
                    {selectedProduct.is_on_sale && selectedProduct.sale_price ? (
                      <>
                        <span className="text-base text-slate-400 line-through">{formatCurrency(selectedProduct.price)}</span>
                        <span className="text-xl font-bold text-red-500">{formatCurrency(selectedProduct.sale_price)}</span>
                      </>
                    ) : selectedProduct.has_variations && selectedProduct.variations?.length > 0 ? (
                      selectedVariation ? (
                        <span className="text-xl font-bold" style={{ color: store.primary_color }}>{formatCurrency(selectedVariation.price)}</span>
                      ) : (
                        <span className="text-xl font-bold" style={{ color: store.primary_color }}>
                          <span className="text-sm font-normal text-slate-500 mr-1">a partir de</span>
                          {formatCurrency(Math.min(...selectedProduct.variations.map(v => v.price)))}
                        </span>
                      )
                    ) : (
                      <span className="text-xl font-bold" style={{ color: store.primary_color }}>{formatCurrency(selectedProduct.price)}</span>
                    )}
                  </div>

                  {/* Variations */}
                  {selectedProduct.has_variations && selectedProduct.variations?.length > 0 && (
                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold flex items-center gap-2">
                          <Layers className="w-4 h-4 text-indigo-500" />
                          Escolha uma opção
                          <span className="text-xs text-red-500 font-normal">(obrigatório)</span>
                        </h4>
                      </div>
                      <RadioGroup
                        value={selectedVariation?.id || ''}
                        onValueChange={(value) => setSelectedVariation(selectedProduct.variations.find(v => v.id === value))}
                        className="space-y-2"
                      >
                        {selectedProduct.variations.map(variation => (
                          <label key={variation.id} className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                            selectedVariation?.id === variation.id ? 'bg-indigo-50 border-2 border-indigo-300' : 'bg-slate-50 hover:bg-slate-100 border border-transparent'
                          }`}>
                            <div className="flex items-center gap-3">
                              <RadioGroupItem value={variation.id} id={`variation-${variation.id}`} data-testid={`variation-${variation.id}`} />
                              <span className="font-medium">{variation.name}</span>
                            </div>
                            <span className="font-semibold" style={{ color: store.primary_color }}>{formatCurrency(variation.price)}</span>
                          </label>
                        ))}
                      </RadioGroup>
                    </div>
                  )}

                  {/* Addons */}
                  {getProductAddons(selectedProduct).length > 0 && (
                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold">Adicionais</h4>
                        {selectedProduct.free_addons_limit > 0 && (
                          <span className="text-sm px-2 py-1 bg-green-100 text-green-700 rounded-full">
                            {getRemainingFreeAddons() > 0 ? `${getRemainingFreeAddons()} grátis restante(s)` : 'Limite de grátis atingido'}
                          </span>
                        )}
                      </div>
                      <div className="space-y-2">
                        {getProductAddons(selectedProduct).map(addon => {
                          const isSelected = selectedAddons.some(a => a.id === addon.id);
                          const wouldBeFree = isAddonFree(addon);
                          const isPremium = addon.is_premium;
                          const isUnavailable = addon.is_available === false;

                          if (isUnavailable) {
                            return (
                              <div key={addon.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-100 opacity-60 cursor-not-allowed">
                                <div className="flex items-center gap-3">
                                  <Checkbox checked={false} disabled data-testid={`addon-${addon.id}`} />
                                  <span className="line-through text-slate-500">{addon.name}</span>
                                  <span className="text-xs px-2 py-0.5 bg-red-500 text-white rounded-full">INDISPONÍVEL</span>
                                </div>
                                {addon.price > 0 && <span className="text-sm font-medium line-through text-slate-400">+ {formatCurrency(addon.price)}</span>}
                              </div>
                            );
                          }

                          return (
                            <label key={addon.id} className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                              isSelected
                                ? wouldBeFree && !isPremium ? 'bg-green-50 border border-green-200' : isPremium ? 'bg-amber-50 border border-amber-200' : 'bg-slate-100 border border-slate-200'
                                : 'bg-slate-50 hover:bg-slate-100'
                            }`}>
                              <div className="flex items-center gap-3">
                                <Checkbox checked={isSelected} onCheckedChange={() => handleAddonToggle(addon)} data-testid={`addon-${addon.id}`} />
                                <span>{addon.name}</span>
                                {isPremium && <span className="text-xs px-2 py-0.5 bg-amber-500 text-white rounded-full">PREMIUM</span>}
                                {!isPremium && wouldBeFree && isSelected && <span className="text-xs px-2 py-0.5 bg-green-500 text-white rounded-full">GRÁTIS</span>}
                              </div>
                              {addon.price > 0 && (
                                <span className={`text-sm font-medium ${!isPremium && wouldBeFree && isSelected ? 'line-through text-slate-400' : ''}`}>
                                  + {formatCurrency(addon.price)}
                                </span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  <div className="border-t pt-4">
                    <Label htmlFor="notes">Observações</Label>
                    <Textarea id="notes" value={productNotes} onChange={(e) => setProductNotes(e.target.value)} placeholder="Ex: Sem cebola, bem passado..." data-testid="product-notes" />
                  </div>

                  {/* Quantity + Add */}
                  <div className="flex items-center justify-between border-t pt-4">
                    <div className="flex items-center gap-3">
                      <Button size="icon" variant="outline" onClick={() => setProductQuantity(Math.max(1, productQuantity - 1))} data-testid="decrease-quantity"><Minus className="w-4 h-4" /></Button>
                      <span className="text-lg font-semibold w-8 text-center">{productQuantity}</span>
                      <Button size="icon" variant="outline" onClick={() => setProductQuantity(productQuantity + 1)} data-testid="increase-quantity"><Plus className="w-4 h-4" /></Button>
                    </div>
                    <Button onClick={handleAddToCart} style={{ backgroundColor: store.primary_color }} className="hover:opacity-90" data-testid="add-to-cart-btn">
                      Adicionar {formatCurrency(getProductTotal())}
                    </Button>
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>

          {/* ─── Cart Sheet ─── */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetContent className="w-full sm:max-w-lg" data-testid="cart-sheet">
              <SheetHeader>
                <SheetTitle>Seu Carrinho</SheetTitle>
              </SheetHeader>

              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64">
                  <ShoppingCart className="w-12 h-12 text-slate-300 mb-4" />
                  <p className="text-slate-500">Carrinho vazio</p>
                </div>
              ) : (
                <div className="flex flex-col h-full">
                  <div className="flex-1 overflow-y-auto py-4 space-y-4">
                    {items.map((item, index) => {
                      const freeLimit = item.product.free_addons_limit || 0;
                      const displayPrice = item.selectedVariation ? item.selectedVariation.price : item.product.price;
                      return (
                        <div key={index} className="flex gap-3 p-3 bg-slate-50 rounded-lg" data-testid={`cart-item-${index}`}>
                          <div className="flex-1">
                            <h4 className="font-semibold">{item.product.name}</h4>
                            {item.selectedVariation && (
                              <p className="text-sm text-indigo-600 flex items-center gap-1"><Layers className="w-3 h-3" />{item.selectedVariation.name}</p>
                            )}
                            <p className="text-sm text-slate-500">{formatCurrency(displayPrice)}</p>
                            {item.selectedAddons.length > 0 && (
                              <div className="text-xs mt-1 space-y-0.5">
                                {item.selectedAddons.map((addon, ai) => (
                                  <div key={ai} className="text-slate-500">
                                    + {addon.name}
                                    {freeLimit > 0 && ai < freeLimit ? <span className="ml-1 text-green-600">(grátis)</span> : addon.price > 0 ? <span className="ml-1 text-slate-400">({formatCurrency(addon.price)})</span> : null}
                                  </div>
                                ))}
                              </div>
                            )}
                            {item.notes && <p className="text-xs text-slate-400 italic mt-1">Obs: {item.notes}</p>}
                            <p className="text-sm font-medium mt-2 text-slate-700">Subtotal: {formatCurrency(getItemPrice(item))}</p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <div className="flex items-center gap-2">
                              <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => updateQuantity(index, item.quantity - 1)}><Minus className="w-3 h-3" /></Button>
                              <span className="w-6 text-center">{item.quantity}</span>
                              <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => updateQuantity(index, item.quantity + 1)}><Plus className="w-3 h-3" /></Button>
                            </div>
                            <Button size="sm" variant="ghost" className="text-red-500 h-8 px-2" onClick={() => removeItem(index)}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Cart Footer */}
                  <div className="border-t pt-4 space-y-3">
                    {deliverySettings.free_threshold > 0 && checkoutData.delivery_type === 'delivery' && (
                      <div className="bg-slate-50 rounded-lg p-3">
                        {getDeliveryInfo().isFree && getDeliveryInfo().reason === 'threshold' ? (
                          <div className="flex items-center gap-2 text-green-600"><Check className="w-4 h-4" /><span className="text-sm font-medium">Parabéns! Frete grátis!</span></div>
                        ) : (
                          <>
                            <div className="flex items-center justify-between text-sm mb-2">
                              <span className="text-slate-600">Frete grátis a partir de {formatCurrency(deliverySettings.free_threshold)}</span>
                              <span className="text-slate-500">Faltam {formatCurrency(getDeliveryInfo().remaining || 0)}</span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2">
                              <div className="h-2 rounded-full transition-all duration-300" style={{ width: `${Math.min((getTotal() / deliverySettings.free_threshold) * 100, 100)}%`, backgroundColor: store.primary_color }} />
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    <div className="flex justify-between text-sm"><span className="text-slate-600">Subtotal</span><span>{formatCurrency(getTotal())}</span></div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Taxa de Entrega</span>
                      {checkoutData.delivery_type === 'pickup' ? <span className="text-green-600 font-medium">Retirada - Grátis</span> : getDeliveryInfo().isFree ? <span className="text-green-600 font-medium">Grátis</span> : <span>{formatCurrency(calculateDeliveryFee())}</span>}
                    </div>
                    {checkoutData.delivery_type === 'delivery' && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs">
                        <p className="text-amber-700 flex items-start gap-2"><AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>A taxa de entrega pode ser ajustada pelo estabelecimento conforme a distância.</span></p>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-bold pt-2 border-t"><span>Total</span><span style={{ color: store.primary_color }}>{formatCurrency(getOrderTotal())}</span></div>

                    {!isStoreOpen && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                        <p className="text-red-700 font-medium flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Loja fechada no momento</p>
                        {storeStatus?.reopen_time && <p className="text-red-600 mt-1">Previsão de reabertura: {storeStatus.reopen_time}</p>}
                      </div>
                    )}

                    <Button className="w-full" style={{ backgroundColor: isStoreOpen ? store.primary_color : '#9CA3AF' }} onClick={() => { if (!isStoreOpen) { toast.error('A loja está fechada no momento'); return; } setIsOpen(false); setShowCheckout(true); }} disabled={!isStoreOpen} data-testid="checkout-btn">
                      {isStoreOpen ? 'Finalizar Pedido' : 'Loja Fechada'}
                    </Button>

                    {!isStoreOpen && store?.whatsapp && (
                      <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors">
                        <MessageCircle className="w-4 h-4" /> Falar no WhatsApp
                      </a>
                    )}
                  </div>
                </div>
              )}
            </SheetContent>
          </Sheet>

          {/* ─── Checkout Modal ─── */}
          <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="checkout-modal">
              <DialogHeader><DialogTitle>Finalizar Pedido</DialogTitle></DialogHeader>
              <form onSubmit={handleCheckout} className="space-y-4">
                <div>
                  <Label htmlFor="name">Seu Nome *</Label>
                  <Input id="name" value={checkoutData.customer_name} onChange={(e) => setCheckoutData(prev => ({ ...prev, customer_name: e.target.value }))} required data-testid="checkout-name" />
                </div>
                <div>
                  <Label htmlFor="phone">Telefone *</Label>
                  <Input id="phone" value={checkoutData.customer_phone} onChange={(e) => setCheckoutData(prev => ({ ...prev, customer_phone: e.target.value }))} placeholder="(11) 99999-9999" required data-testid="checkout-phone" />
                </div>

                <div>
                  <Label>Tipo de Pedido</Label>
                  <RadioGroup value={checkoutData.delivery_type} onValueChange={(value) => setCheckoutData(prev => ({ ...prev, delivery_type: value }))} className="flex gap-4 mt-2">
                    <div className={`flex items-center space-x-2 p-3 rounded-lg flex-1 border ${checkoutData.delivery_type === 'delivery' ? 'border-2' : ''}`} style={checkoutData.delivery_type === 'delivery' ? { borderColor: store.primary_color } : {}}>
                      <RadioGroupItem value="delivery" id="delivery" data-testid="delivery-option" />
                      <div>
                        <Label htmlFor="delivery" className="font-medium">Entrega</Label>
                        {deliverySettings.default_fee > 0 && <p className="text-xs text-slate-500">{getDeliveryInfo().isFree && getDeliveryInfo().reason === 'threshold' ? 'Grátis!' : `+ ${formatCurrency(deliverySettings.default_fee)}`}</p>}
                      </div>
                    </div>
                    <div className={`flex items-center space-x-2 p-3 rounded-lg flex-1 border ${checkoutData.delivery_type === 'pickup' ? 'border-2' : ''}`} style={checkoutData.delivery_type === 'pickup' ? { borderColor: store.primary_color } : {}}>
                      <RadioGroupItem value="pickup" id="pickup" data-testid="pickup-option" />
                      <div>
                        <Label htmlFor="pickup" className="font-medium">Retirada</Label>
                        <p className="text-xs text-green-600">Grátis</p>
                      </div>
                    </div>
                  </RadioGroup>
                </div>

                {checkoutData.delivery_type === 'delivery' && (
                  <div className="space-y-4">
                    {deliveryZones.length > 0 && (
                      <div>
                        <Label htmlFor="delivery-zone" className="flex items-center gap-2"><Navigation className="w-4 h-4" />Região de Entrega *</Label>
                        <Select value={selectedZone || ''} onValueChange={(value) => setSelectedZone(value)}>
                          <SelectTrigger className="w-full mt-1" data-testid="delivery-zone-select"><SelectValue placeholder="Selecione sua região" /></SelectTrigger>
                          <SelectContent>
                            {deliveryZones.map((zone) => (
                              <SelectItem key={zone.id} value={zone.id}>
                                <div className="flex items-center justify-between w-full gap-4">
                                  <span>{zone.name}</span>
                                  <span className="text-slate-500 text-sm">{zone.fee > 0 ? formatCurrency(zone.fee) : 'Grátis'}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {selectedZone && <p className="text-xs text-slate-500 mt-1">Taxa: <span className="font-medium" style={{ color: store.primary_color }}>{formatCurrency(calculateDeliveryFee())}</span></p>}
                        {!selectedZone && <p className="text-xs text-amber-600 mt-1">Selecione sua região para calcular a taxa</p>}
                      </div>
                    )}
                    <div>
                      <Label htmlFor="address" className="flex items-center gap-2"><MapPin className="w-4 h-4" />Endereço de Entrega *</Label>
                      <Textarea id="address" value={checkoutData.customer_address} onChange={(e) => setCheckoutData(prev => ({ ...prev, customer_address: e.target.value }))} placeholder="Rua, número, complemento, ponto de referência..." required={checkoutData.delivery_type === 'delivery'} className="mt-1" rows={3} data-testid="checkout-address" />
                      <p className="text-xs text-slate-500 mt-1">Informe o endereço completo com pontos de referência</p>
                    </div>
                  </div>
                )}

                <div>
                  <Label>Forma de Pagamento</Label>
                  <RadioGroup value={checkoutData.payment_method} onValueChange={(value) => setCheckoutData(prev => ({ ...prev, payment_method: value }))} className="mt-2 space-y-2">
                    <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg">
                      <RadioGroupItem value="cash" id="cash" data-testid="cash-option" /><Label htmlFor="cash">Dinheiro</Label>
                    </div>
                    <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg">
                      <RadioGroupItem value="card" id="card" data-testid="card-option" /><Label htmlFor="card">Cartão na entrega</Label>
                    </div>
                    <div className={`flex items-center space-x-2 p-3 rounded-lg ${checkoutData.payment_method === 'pix' ? 'bg-green-50 border border-green-200' : 'bg-slate-50'}`}>
                      <RadioGroupItem value="pix" id="pix" data-testid="pix-option" />
                      <Label htmlFor="pix" className="flex items-center gap-2"><QrCode className="w-4 h-4 text-green-600" />PIX</Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* PIX Payment Info */}
                {checkoutData.payment_method === 'pix' && store.pix_key && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-4" data-testid="pix-payment-section">
                    <div className="flex items-center gap-2 text-green-800"><QrCode className="w-5 h-5" /><h4 className="font-semibold">Pagamento via PIX</h4></div>
                    <div className="flex flex-col items-center bg-white rounded-lg p-4">
                      <QRCodeSVG value={store.pix_key} size={150} level="M" />
                      <p className="text-xs text-slate-500 mt-2">Escaneie o QR Code para pagar</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Chave PIX ({store.pix_key_type?.toUpperCase() || 'CPF'})</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-sm font-mono bg-slate-100 px-2 py-1 rounded break-all">{store.pix_key}</code>
                        <Button type="button" variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(store.pix_key); toast.success('Chave PIX copiada!'); }} data-testid="copy-pix-key-btn"><Copy className="w-4 h-4" /></Button>
                      </div>
                      {store.pix_beneficiary_name && <p className="text-xs text-slate-500 mt-2">Beneficiário: <strong>{store.pix_beneficiary_name}</strong></p>}
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                      <p className="font-medium text-amber-800 mb-2">Instruções:</p>
                      <ol className="list-decimal list-inside text-amber-700 space-y-1 text-xs">
                        <li>Faça o PIX usando o QR Code ou a chave acima</li>
                        <li>Valor total: <strong>{formatCurrency(getOrderTotal())}</strong></li>
                        <li>Após pagar, envie o comprovante pelo WhatsApp</li>
                        <li>Seu pedido será confirmado após verificação</li>
                      </ol>
                    </div>
                    {store.whatsapp && (
                      <Button type="button" className="w-full bg-green-500 hover:bg-green-600" onClick={() => { const phone = store.whatsapp.replace(/\D/g, ''); const message = encodeURIComponent(`Olá! Fiz um pedido via PIX.\n\nValor: ${formatCurrency(getOrderTotal())}\nNome: ${checkoutData.customer_name || '(preencher)'}\nTelefone: ${checkoutData.customer_phone || '(preencher)'}\n\nSegue o comprovante de pagamento:`); window.open(`https://wa.me/${phone}?text=${message}`, '_blank'); }} data-testid="send-pix-receipt-btn">
                        <MessageCircle className="w-4 h-4 mr-2" />Enviar Comprovante via WhatsApp
                      </Button>
                    )}
                  </div>
                )}

                {checkoutData.payment_method === 'pix' && !store.pix_key && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800" data-testid="pix-not-configured">
                    <p className="font-medium flex items-center gap-2"><AlertTriangle className="w-4 h-4" />PIX não configurado</p>
                    <p className="text-sm mt-1">Entre em contato com o estabelecimento para obter as informações de pagamento PIX.</p>
                    {store.whatsapp && (
                      <Button type="button" variant="outline" className="mt-2" onClick={() => { const phone = store.whatsapp.replace(/\D/g, ''); const message = encodeURIComponent(`Olá! Gostaria de fazer um pedido via PIX. Podem me enviar os dados para pagamento?`); window.open(`https://wa.me/${phone}?text=${message}`, '_blank'); }}>
                        <MessageCircle className="w-4 h-4 mr-2" />Falar no WhatsApp
                      </Button>
                    )}
                  </div>
                )}

                <div>
                  <Label htmlFor="notes">Observações</Label>
                  <Textarea id="notes" value={checkoutData.notes} onChange={(e) => setCheckoutData(prev => ({ ...prev, notes: e.target.value }))} placeholder="Alguma informação adicional..." data-testid="checkout-notes" />
                </div>

                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-slate-600">Subtotal</span><span>{formatCurrency(getTotal())}</span></div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Taxa de Entrega</span>
                    {checkoutData.delivery_type === 'pickup' ? <span className="text-green-600 font-medium">Retirada - Grátis</span> : getDeliveryInfo().isFree ? <span className="text-green-600 font-medium">Grátis</span> : <span>{formatCurrency(calculateDeliveryFee())}</span>}
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2 border-t"><span>Total</span><span style={{ color: store.primary_color }}>{formatCurrency(getOrderTotal())}</span></div>
                  <Button type="submit" className="w-full mt-4" style={{ backgroundColor: store.primary_color }} data-testid="submit-order-btn">Confirmar Pedido</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}

export default function StorePage() {
  return (
    <CartProvider>
      <StoreContent />
    </CartProvider>
  );
}
